import { FunctionalPermissionKey } from '@/helper/FunctionalPermissions';
import { WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

export const subjectLabelMap: Record<WorkspaceSubjects, string> = {
  [WorkspaceSubjects.Academy]: 'academy',
  [WorkspaceSubjects.Announcement]: 'announcement',
  [WorkspaceSubjects.Learner]: 'learner',
  [WorkspaceSubjects.Assignment]: 'assignment',
  [WorkspaceSubjects.Attendance]: 'attendance',
  [WorkspaceSubjects.Certificate]: 'certificate',
  [WorkspaceSubjects.Community]: 'community',
  [WorkspaceSubjects.ContentCollection]: 'collection',
  [WorkspaceSubjects.Event]: 'session',
  [WorkspaceSubjects.Group]: 'group',
  [WorkspaceSubjects.Homepage]: 'homepage',
  [WorkspaceSubjects.Members]: 'members',
  [WorkspaceSubjects.Post]: 'post',
  [WorkspaceSubjects.Resource]: 'content',
  [WorkspaceSubjects.Role]: 'staff role',
  [WorkspaceSubjects.Scorecard]: 'scorecard',
  [WorkspaceSubjects.Space]: 'space',
  [WorkspaceSubjects.Staff]: 'staff',
  [WorkspaceSubjects.Tag]: 'tag',
  [WorkspaceSubjects.User]: 'user',
  [WorkspaceSubjects.UserCollection]: 'group',
  [WorkspaceSubjects.Workspace]: 'workspace',
  [WorkspaceSubjects.Channel]: 'channel',
  [WorkspaceSubjects.Report]: 'report',
  [WorkspaceSubjects.SharedConnection]: 'shared connection',
  [WorkspaceSubjects.ContentSubworkspace]: 'content subworkspace',
  [WorkspaceSubjects.EventSubworkspace]: 'event subworkspace',
  [WorkspaceSubjects.Authentication]: 'authentication',
  [WorkspaceSubjects.OkrCycle]: 'cycle',
  [WorkspaceSubjects.Skill]: 'skill',
  [WorkspaceSubjects.SkillMatrix]: 'skill matrix',
  [WorkspaceSubjects.CompanyObjective]: 'company objective',
  [WorkspaceSubjects.DepartmentObjective]: 'department objective',
  [WorkspaceSubjects.EmailDigest]: 'email digest',
  [WorkspaceSubjects.IndividualObjective]: 'individual objective',
  [WorkspaceSubjects.Okr]: 'okr',
  [WorkspaceSubjects.TeamObjective]: 'team objective',
  [WorkspaceSubjects.Integration]: 'integrations',
  [WorkspaceSubjects.Billing]: 'billing',
  [WorkspaceSubjects.Subaccount]: 'subaccounts',
  [WorkspaceSubjects.ReviewCycle]: 'review cycle',
  [WorkspaceSubjects.Department]: 'department',
  [WorkspaceSubjects.File]: 'file',
  [WorkspaceSubjects.Category]: 'category',
  [WorkspaceSubjects.TagSubworkspace]: 'tag subworkspace',
  [WorkspaceSubjects.Recordings]: 'recordings',
  [WorkspaceSubjects.UserSurvey]: 'survey',
  [WorkspaceSubjects.AuditLog]: 'audit log',
  [WorkspaceSubjects.Journey]: 'journey',
  [WorkspaceSubjects.Metric]: 'metric',
  [WorkspaceSubjects.Gamification]: 'gamification',
};

export const actionLabelMap: Record<WorkspaceActions, string> = {
  [WorkspaceActions.Archive]: 'ARCHIVE',
  [WorkspaceActions.Create]: 'CREATE',
  [WorkspaceActions.Edit]: 'EDIT',
  [WorkspaceActions.Hide]: 'HIDE',
  [WorkspaceActions.Invite]: 'INVITE',
  [WorkspaceActions.List]: 'LIST',
  [WorkspaceActions.Manage]: 'MANAGE',
  [WorkspaceActions.Pin]: 'PIN',
  [WorkspaceActions.Publish]: 'PUBLISH',
  [WorkspaceActions.Read]: 'VIEW',
  [WorkspaceActions.Remove]: 'DELETE',
  [WorkspaceActions.Enroll]: 'ENROLL',
  [WorkspaceActions.Approve]: 'APPROVE',
  [WorkspaceActions.Reject]: 'REJECT',
  [WorkspaceActions.Send]: 'SEND',
  [WorkspaceActions.ShareContent]: 'SHARE',
  [WorkspaceActions.Masquerade]: 'MASQUERADE',
  [WorkspaceActions.Attach]: 'ATTACH',
  [WorkspaceActions.Checkin]: 'CHECKIN',
  [WorkspaceActions.Merge]: 'MERGE',
};

/**
 * Functional permission that governs a legacy workspace action for the learning
 * entities covered by the permission mapping (content, collections, sessions,
 * assignments). Subjects and actions that the mapping doesn't cover return
 * `undefined`, which leaves the legacy workspace check in charge everywhere.
 */
const functionalPermissionMap: Partial<
  Record<WorkspaceSubjects, Partial<Record<WorkspaceActions, FunctionalPermissionKey>>>
> = {
  [WorkspaceSubjects.Resource]: {
    [WorkspaceActions.Read]: FunctionalPermissionKey.ViewContent,
    [WorkspaceActions.List]: FunctionalPermissionKey.ViewAllContent,
    [WorkspaceActions.Create]: FunctionalPermissionKey.AuthorContent,
    [WorkspaceActions.Edit]: FunctionalPermissionKey.AuthorContent,
    // Deleting content is gated by PUBLISH_CONTENT, alongside publish and archive.
    [WorkspaceActions.Remove]: FunctionalPermissionKey.PublishContent,
    [WorkspaceActions.Publish]: FunctionalPermissionKey.PublishContent,
    [WorkspaceActions.Archive]: FunctionalPermissionKey.PublishContent,
  },
  [WorkspaceSubjects.ContentCollection]: {
    [WorkspaceActions.Read]: FunctionalPermissionKey.ViewCollection,
    [WorkspaceActions.List]: FunctionalPermissionKey.ViewAllCollection,
    [WorkspaceActions.Create]: FunctionalPermissionKey.ManageCollection,
    [WorkspaceActions.Edit]: FunctionalPermissionKey.ManageCollection,
    [WorkspaceActions.Remove]: FunctionalPermissionKey.ManageCollection,
    [WorkspaceActions.Archive]: FunctionalPermissionKey.ManageCollection,
  },
  [WorkspaceSubjects.Event]: {
    [WorkspaceActions.Read]: FunctionalPermissionKey.ViewSession,
    [WorkspaceActions.List]: FunctionalPermissionKey.ViewAllSession,
    [WorkspaceActions.Create]: FunctionalPermissionKey.ManageSession,
    [WorkspaceActions.Edit]: FunctionalPermissionKey.ManageSession,
    // Cancelling a session is an Archive, and the mapping keeps that on MANAGE_SESSION.
    [WorkspaceActions.Archive]: FunctionalPermissionKey.ManageSession,
    [WorkspaceActions.Remove]: FunctionalPermissionKey.DeleteSession,
  },
  [WorkspaceSubjects.Attendance]: {
    [WorkspaceActions.Manage]: FunctionalPermissionKey.MarkSessionAttendance,
  },
  [WorkspaceSubjects.Recordings]: {
    [WorkspaceActions.Manage]: FunctionalPermissionKey.ManageSessionRecordings,
  },
  [WorkspaceSubjects.Assignment]: {
    [WorkspaceActions.Read]: FunctionalPermissionKey.ViewAssignment,
    [WorkspaceActions.List]: FunctionalPermissionKey.ViewAllAssignment,
    [WorkspaceActions.Create]: FunctionalPermissionKey.ManageAssignments,
    [WorkspaceActions.Edit]: FunctionalPermissionKey.ManageAssignments,
    [WorkspaceActions.Remove]: FunctionalPermissionKey.ManageAssignments,
  },
};

export const getFunctionalPermission = (
  subject: WorkspaceSubjects,
  action: WorkspaceActions,
): FunctionalPermissionKey | undefined => functionalPermissionMap[subject]?.[action];
